import { FC, useEffect, useMemo, useState, useRef, memo } from "react"
import { Info, InfoSettings, LongShortContainer, TitlesContainer, UpperInfoStyled } from "./UpperInfo.styled"
import {themeStore, RED, typeOptions, showNotification, getEnterSpread, getCloseSpread, getGeneralTitles, getLongShortTitles, IStatistics, TType, UpperInfoProps, LongShortStatistics} from "@root/common";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";

const NOTIFICATIONS_DELAY_MS = 1 * 60 * 1e3

interface IUpperInfo {
    statistics?: LongShortStatistics;
    type: TType;
    coin: string
    isVisible: boolean;
}

const UpperInfo: FC<UpperInfoProps> = observer((props) => {
    const {statistics, type, coin, isVisible} = props as IUpperInfo
    const inputRef = useRef(null)
    const outputRef = useRef(null)
    const volumeRef = useRef(null)

    const [input, setInput] = useState('')
    const [output, setOutput] = useState('')
    const [volume, setVolume] = useState(0)

    const generalTitles: Record<string, string | null> = useMemo(() => {
        const titles = getGeneralTitles(type, statistics, volume)
        return titles
    }, [type, statistics, volume])

    const lastNotificationSentTime = useRef<number>(0)
    const delayTimeout = useRef<NodeJS.Timeout>()
    useEffect(() => {
      const generalTitlesInput = parseFloat(generalTitles["Вход"] || '') || -Infinity
      const generalTitlesOutput = parseFloat(generalTitles["Выход"] || '') || -Infinity

      if (input && generalTitlesInput > parseFloat(input) || output && generalTitlesOutput > parseFloat(output)) {
        if (lastNotificationSentTime.current === 0) {
          return;
        }
        if (Date.now() - lastNotificationSentTime.current < NOTIFICATIONS_DELAY_MS) {
          return;
        }
        showNotification({title: `${coin} | ${typeOptions.find(opt => opt.option === type)?.name}`, body: `${generalTitlesInput}/${generalTitlesOutput}% | ${statistics?.long.name} - ${statistics?.short.name}`})
        lastNotificationSentTime.current = Date.now()
      }
    }, [generalTitles])
    useEffect(() => {
      clearTimeout(delayTimeout.current)
      delayTimeout.current = setTimeout(() => {
        if (!input && !output) {
          lastNotificationSentTime.current = 0
        } else {
          lastNotificationSentTime.current = 1;
        }
      }, 1e3)
    }, [input, output])

    useEffect(() => {
        if (isVisible) {
            const enterSpread = statistics ? getEnterSpread(statistics, volume) : ""
            const closeSpread = statistics ? getCloseSpread(statistics, volume) : ""
            document.title = `${enterSpread}|${closeSpread} ${coin} `;
        }
    }, [type, statistics, volume]);

    const volumes = useMemo(() => {
        return [
            statistics?.long.orderbook?.asks?.map(ask => parseInt(ask.volume)).reduce((acc, el) => acc + el, 0),
            statistics?.long.orderbook?.bids?.map(bid => parseInt(bid.volume)).reduce((acc, el) => acc + el, 0),
            statistics?.short.orderbook?.asks?.map(ask => parseInt(ask.volume)).reduce((acc, el) => acc + el, 0),
            statistics?.short.orderbook?.bids?.map(bid => parseInt(bid.volume)).reduce((acc, el) => acc + el, 0),
        ]
    }, [statistics])

    const isVolumeTooLow = useMemo(
      () => !!volumes.find(vol => vol && vol < volume),
      [volumes, volume],
    )

    return (
        <UpperInfoStyled>
            <TitlesContainer>
              <InfoSettings>
                <p style={{ color: themeStore.textColor }}>
                  Вход %:
                  <input
                    value={input}
                    ref={inputRef}
                    type='number'
                    onChange={e => setInput(e.target.value)}
                    onClick={() => {
                      //@ts-ignore
                      inputRef.current.select()
                    }}
                    style={{
                      color: themeStore.textColor,
                      backgroundColor: themeStore.bgColor,
                      border: `1px solid ${themeStore.textColor}`,
                    }}
                  />
                </p>
                <p style={{ color: themeStore.textColor }}>
                  Выход %:
                  <input
                    value={output}
                    ref={outputRef}
                    type="number"
                    onChange={e => setOutput(e.target.value)}
                    onClick={() => {
                      //@ts-ignore
                      outputRef.current.select()
                    }}
                    style={{
                      color: themeStore.textColor,
                      backgroundColor: themeStore.bgColor,
                      border: `1px solid ${themeStore.textColor}`,
                    }}
                  />
                </p>
                <p style={{ color: themeStore.textColor }}>
                  Объем $:
                  <input
                    value={volume}
                    ref={volumeRef}
                    type="number"
                    onChange={e => setVolume(parseFloat(e.target.value) || 0)}
                    onClick={() => {
                      //@ts-ignore
                      volumeRef.current.select()
                    }}
                    style={{
                      color: themeStore.textColor,
                      backgroundColor: themeStore.bgColor,
                      border: `1px solid ${themeStore.textColor}`,
                    }}
                  />
                </p>

                <p
                  style={{
                    color: RED,
                    visibility: isVolumeTooLow ? "visible" : "hidden",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <PriorityHighIcon />
                  Объем &gt; 5 ордеров
                </p>

              </InfoSettings>
              <Info style={{ borderColor: themeStore.textColor }}>
                {Object.keys(generalTitles).map(key => {
                  let color = themeStore.textColor
                  //@ts-ignore
                  if ((key === 'Вход' || key === 'Выход') && +generalTitles[key].replace('%', '') >= 0) color = "rgb(11, 186, 116)"
                  //@ts-ignore
                  if ((key === 'Вход' || key === 'Выход') && +generalTitles[key].replace('%', '')  < 0) color = "#FF4671"
                  return <p key={`titles-${key}`} style={{ color: themeStore.textColor }}>{key}: <span style={{ color }}>{generalTitles[key]}</span></p>
                }
                )}
              </Info>
            </TitlesContainer>

            <LongShortContainer>
                {statistics && [statistics.long, statistics.short].map((longShort, i) =>
                  <div key={longShort.link} style={{ borderColor: themeStore.textColor }}>
                    <LongShortTitle link={longShort.link} name={longShort.name}/>
                    {Object.entries(getLongShortTitles(longShort)).map(entries =>
                        <p key={`longshort-${i}-${entries[0]}`} style={{ color: themeStore.textColor }}>{entries[0]}: {entries[1]}</p>
                    )}
                  </div>
                )}
            </LongShortContainer>
        </UpperInfoStyled>
    )
});

const LongShortTitle = memo((options: {
  link?: string
  name?: string
}) => {
  const {link, name} = options

  return (
    <Link to={link || "/"} target='_blank'>{name}</Link>
  )
}, (prev, next) => {
  return prev.link === next.link && prev.name === next.name
})

export default UpperInfo
